import socket from "../socket";
import eventbus from "../eventbus";
import type {ClientChan, ClientNetwork, ClientUser} from "../types";
import {switchToChannel} from "../router";
import {TypedStore} from "../store";
import useCloseChannel from "../hooks/use-close-channel";
import {ChanType} from "../../../shared/types/chan";

// ЛАФЧЕ.ORG: whoisThenAction() (по-долу) прави вътрешна WHOIS заявка само
// за да извади hostname за mute/unmute/kickban By Host действия - тя НЕ
// трябва да отваря видимия WHOIS модал (App.vue слуша същото събитие
// глобално). Докато ник е в този Set, App.vue пропуска отварянето.
export const suppressWhoisModalFor = new Set<string>();

type BaseContextMenuItem = {
	label: string;
	type: string;
	class: string;
};

type ContextMenuItemWithAction = BaseContextMenuItem & {
	action: () => void;
};

type ContextMenuItemWithLink = BaseContextMenuItem & {
	link?: string;
};
type ContextMenuItemWithSubmenu = BaseContextMenuItem & {
	submenu: ContextMenuItem[];
};

type ContextMenuDividerItem = {
	type: "divider";
};

export type ContextMenuItem =
	| ContextMenuItemWithAction
	| ContextMenuItemWithLink
	| ContextMenuItemWithSubmenu
	| ContextMenuDividerItem;

export function generateChannelContextMenu(
	channel: ClientChan,
	network: ClientNetwork
): ContextMenuItem[] {
	const closeChannel = useCloseChannel(channel);

	const typeMap = {
		lobby: "network",
		channel: "chan",
		query: "query",
		special: "chan",
	};

	const closeMap = {
		lobby: "Премахни",
		channel: "Напусни",
		query: "Затвори",
		special: "Затвори",
	};

	let items: ContextMenuItem[] = [
		{
			label: channel.name,
			type: "item",
			class: typeMap[channel.type],
			link: `/chan-${channel.id}`,
		},
		{
			type: "divider",
		},
	];

	// Add menu items for lobbies
	if (channel.type === ChanType.LOBBY) {
		items = [
			...items,
			{
				label: "Редактирай тази мрежа…",
				type: "item",
				class: "edit",
				link: `/edit-network/${network.uuid}`,
			},
			{
				label: "Влез в канал…",
				type: "item",
				class: "join",
				action: () => (network.isJoinChannelShown = true),
			},
			{
				label: "Списък с всички канали",
				type: "item",
				class: "list",
				action: () =>
					socket.emit("input", {
						target: channel.id,
						text: "/list",
					}),
			},
			{
				label: "Списък с игнорирани потребители",
				type: "item",
				class: "list",
				action: () =>
					socket.emit("input", {
						target: channel.id,
						text: "/ignorelist",
					}),
			},
			network.status.connected
				? {
						label: "Прекъсни връзката",
						type: "item",
						class: "disconnect",
						action: () =>
							socket.emit("input", {
								target: channel.id,
								text: "/disconnect",
							}),
				  }
				: {
						label: "Свържи се",
						type: "item",
						class: "connect",
						action: () =>
							socket.emit("input", {
								target: channel.id,
								text: "/connect",
							}),
				  },
		];
	}

	// Add menu items for channels
	if (channel.type === ChanType.CHANNEL) {
		items.push({
			label: "Редактирай топик",
			type: "item",
			class: "edit",
			action() {
				channel.editTopic = true;
				switchToChannel(channel);
			},
		});
		items.push({
			label: "Бан списък",
			type: "item",
			class: "list",
			action() {
				socket.emit("input", {
					target: channel.id,
					text: "/banlist",
				});
			},
		});
	}

	// Add menu items for queries
	if (channel.type === ChanType.QUERY) {
		items.push(
			{
				label: "Кой е?",
				type: "item",
				class: "action-whois",
				action() {
					switchToChannel(channel);
					socket.emit("input", {
						target: channel.id,
						text: "/whois " + channel.name,
					});
				},
			},
			{
				label: "Игнор",
				type: "item",
				class: "action-ignore",
				action() {
					socket.emit("input", {
						target: channel.id,
						text: "/ignore " + channel.name,
					});
				},
			}
		);
	}

	if (channel.type === ChanType.CHANNEL || channel.type === ChanType.QUERY) {
		items.push({
			label: "Изчисти историята",
			type: "item",
			class: "clear-history",
			action() {
				eventbus.emit(
					"confirm-dialog",
					{
						title: "Изчисти историята",
						text: `Сигурен ли си, че искаш да изчистиш историята за ${channel.name}? Това не може да бъде отменено.`,
						button: "Изчисти историята",
					},
					(result) => {
						if (!result) {
							return;
						}

						socket.emit("history:clear", {
							target: channel.id,
						});
					}
				);
			},
		});
	}

	const humanFriendlyChanTypeMap: Record<string, string> = {
		lobby: "мрежа",
		channel: "канал",
		query: "разговор",
	};

	// We don't allow the muting of ChanType.SPECIAL channels
	const mutableChanTypes = Object.keys(humanFriendlyChanTypeMap);

	if (mutableChanTypes.includes(channel.type)) {
		const chanType = humanFriendlyChanTypeMap[channel.type];

		items.push({
			label: channel.muted ? `Спри заглушаването на ${chanType}` : `Заглуши ${chanType}`,
			type: "item",
			class: "mute",
			action() {
				socket.emit("mute:change", {
					target: channel.id,
					setMutedTo: !channel.muted,
				});
			},
		});
	}

	// Add close menu item
	items.push({
		label: closeMap[channel.type],
		type: "item",
		class: "close",
		action() {
			closeChannel();
		},
	});

	return items;
}

export function generateInlineChannelContextMenu(
	store: TypedStore,
	chan: string,
	network: ClientNetwork
): ContextMenuItem[] {
	const join = () => {
		const channel = network.channels.find((c) => c.name === chan);

		if (channel) {
			switchToChannel(channel);
		}

		if (store.state.activeChannel) {
			socket.emit("input", {
				target: store.state.activeChannel.channel.id,
				text: "/join " + chan,
			});
		} else {
			// eslint-disable-next-line no-console
			console.error("Unable to join channel: activeChannel is undefined");
		}
	};

	const channel = network.channels.find((c) => c.name === chan);

	if (channel) {
		return [
			{
				label: "Отиди в канала",
				type: "item",
				class: "chan",
				link: `/chan-${channel.id}`,
			},
		];
	}

	return [
		{
			label: "Влез в канала",
			type: "item",
			class: "join",
			action: join,
		},
	];
}

export function generateUserContextMenu(
	store: TypedStore,
	channel: ClientChan,
	network: ClientNetwork,
	user: Pick<ClientUser, "nick" | "modes">
): ContextMenuItem[] {
	const currentChannelUser: ClientUser | Record<string, never> = channel
		? channel.users.find((u) => u.nick === network.nick) || {}
		: {};

	const whois = () => {
		const chan = network.channels.find((c) => c.name === user.nick);

		if (chan) {
			switchToChannel(chan);
		}

		socket.emit("input", {
			target: channel.id,
			text: "/whois " + user.nick,
		});
	};

	const items: ContextMenuItem[] = [
		{
			label: user.nick,
			type: "item",
			class: "user",
			action: whois,
		},
		{
			type: "divider",
		},
		{
			label: "Кой е?",
			type: "item",
			class: "action-whois",
			action: whois,
		},
		{
			label: "Игнор",
			type: "item",
			class: "action-ignore",
			action() {
				socket.emit("input", {
					target: channel.id,
					text: "/ignore " + user.nick,
				});
			},
		},
		{
			label: "Лична",
			type: "item",
			class: "action-query",
			action() {
				const chan = store.getters.findChannelOnCurrentNetwork(user.nick);

				if (chan) {
					switchToChannel(chan);
				}

				socket.emit("input", {
					target: channel.id,
					text: "/query " + user.nick,
				});
			},
		},
		{
			label: "Шамари ▸",
			type: "item",
			class: "action-slap-submenu",
			submenu: [
				{
					label: "Рестарт бутон",
					type: "item",
					class: "action-slap",
					action() {
						socket.emit("input", {
							target: channel.id,
							text: "/slap " + user.nick,
						});
					},
				},
				{
					label: "Ping -f (Събуди го)",
					type: "item",
					class: "action-slap",
					action() {
						socket.emit("input", {
							target: channel.id,
							text: "/slap2 " + user.nick,
						});
					},
				},
				{
					label: "Изтрий System32",
					type: "item",
					class: "action-slap",
					action() {
						socket.emit("input", {
							target: channel.id,
							text: "/slap3 " + user.nick,
						});
					},
				},
				{
					label: "Шут в захранването",
					type: "item",
					class: "action-slap",
					action() {
						socket.emit("input", {
							target: channel.id,
							text: "/slap4 " + user.nick,
						});
					},
				},
				{
					label: "Дефектен суич",
					type: "item",
					class: "action-slap",
					action() {
						socket.emit("input", {
							target: channel.id,
							text: "/slap5 " + user.nick,
						});
					},
				},
				{
					label: "Затвори портовете",
					type: "item",
					class: "action-slap",
					action() {
						socket.emit("input", {
							target: channel.id,
							text: "/slap6 " + user.nick,
						});
					},
				}
			],
		},
	];

	// Bail because we're in a query or we don't have a special mode.
	if (!currentChannelUser.modes || currentChannelUser.modes.length < 1) {
		return items;
	}

	// Names of the standard modes we are able to change
	const modeCharToName = {
		"~": "Шеф",
		"&": "Админ",
		"@": "Оп",
		"%": "полу-оп",
		"+": "глас",
	};

	// Labels for the mode changes.  For example .rev({mode: "a", symbol: "&"}) => 'Отнеми администратор (-a)'
	const modeTextTemplate = {
		revoke(m: {symbol: string; mode: string}) {
			const name = modeCharToName[m.symbol];

			if (typeof name !== "string") {
				return "";
			}

			const res = name ? `Махни ${name} (-${m.mode})` : `Режим -${m.mode}`;
			return res;
		},
		give(m: {symbol: string; mode: string}) {
			const name = modeCharToName[m.symbol];

			if (typeof name !== "string") {
				return "";
			}

			const res = name ? `Дай ${name} (+${m.mode})` : `Режим +${m.mode}`;
			return res;
		},
	};

	const networkModeSymbols = network.serverOptions.PREFIX.symbols;

	/**
	 * Determine whether the prefix of mode p1 has access to perform actions on p2.
	 *
	 * EXAMPLE:
	 *    compare('@', '@') => true
	 *    compare('&', '@') => true
	 *    compare('+', '~') => false
	 * @param {string} p1 The mode performing an action
	 * @param {string} p2 The target mode
	 *
	 * @return {boolean} whether p1 can perform an action on p2
	 */
	function compare(p1: string, p2: string): boolean {
		// The modes ~ and @ can perform actions on their own mode.  The others on modes below.
		return "~@".indexOf(p1) > -1
			? networkModeSymbols.indexOf(p1) <= networkModeSymbols.indexOf(p2)
			: networkModeSymbols.indexOf(p1) < networkModeSymbols.indexOf(p2);
	}

	const modeItems: ContextMenuItem[] = [];
	network.serverOptions.PREFIX.prefix.forEach((mode) => {
		if (!compare(currentChannelUser.modes[0], mode.symbol)) {
			// Our highest mode is below the current mode.  Bail.
			return;
		}

		if (!user.modes.includes(mode.symbol)) {
			// The target doesn't already have this mode, therefore we can set it.
			modeItems.push({
				label: modeTextTemplate.give(mode),
				type: "item",
				class: "action-set-mode",
				action() {
					socket.emit("input", {
						target: channel.id,
						text: "/mode +" + mode.mode + " " + user.nick,
					});
				},
			});
		} else {
			modeItems.push({
				label: modeTextTemplate.revoke(mode),
				type: "item",
				class: "action-revoke-mode",
				action() {
					socket.emit("input", {
						target: channel.id,
						text: "/mode -" + mode.mode + " " + user.nick,
					});
				},
			});
		}
	});
	if (modeItems.length > 0) {
		items.push({
			label: "Модове ▸",
			type: "item",
			class: "action-modes-submenu",
			submenu: modeItems,
		});
	}
	// Determine if we are half-op or op depending on the network modes so we can kick.
	if (!compare(networkModeSymbols.indexOf("%") > -1 ? "%" : "@", currentChannelUser.modes[0])) {
		// Check if the target user has no mode or a mode lower than ours.
		if (user.modes.length === 0 || compare(currentChannelUser.modes[0], user.modes[0])) {
			// ЛАФЧЕ.ORG: WHOIS lookup за "By Host" опциите - хостът на потребителя
			// не идва в user обекта, само в WHOIS отговор. Слушаме еднократно за
			// следващия WHOIS отговор за точно този ник, извличаме hostname-а,
			// после изпращаме желаната команда.
			const whoisThenAction = (callback: (hostname: string) => void) => {
				suppressWhoisModalFor.add(user.nick);

				const handler = (data: any) => {
					if (
						data.msg &&
						data.msg.type === "whois" &&
						data.msg.whois &&
						data.msg.whois.nick === user.nick
					) {
						socket.off("msg", handler);
						suppressWhoisModalFor.delete(user.nick);
						callback(data.msg.whois.hostname as string);
						// ЛАФЧЕ.ORG: /whois автоматично отваря/фокусира query таб с
						// потребителя (сървърна логика, "did a whois" страничен ефект) -
						// връщаме фокуса обратно към реалния канал веднага след това
						switchToChannel(channel);
					}
				};
				socket.on("msg", handler);
				socket.emit("input", {
					target: channel.id,
					text: "/whois " + user.nick,
				});
			};

			items.push({
				label: "Изритай ▸",
				type: "item",
				class: "action-kick-submenu",
				submenu: [
					{
						label: "Mute (By Nick)",
						type: "item",
						class: "action-mute-nick",
						action() {
							socket.emit("input", {
								target: channel.id,
								text: "/mode +b m:" + user.nick + "!*@*",
							});
						},
					},
					{
						label: "Mute (By Host)",
						type: "item",
						class: "action-mute-host",
						action() {
							whoisThenAction((hostname) => {
								socket.emit("input", {
									target: channel.id,
									text: "/mode +b m:*!*@" + hostname,
								});
							});
						},
					},
					{
						label: "Unmute",
						type: "item",
						class: "action-unmute",
						action() {
							// ЛАФЧЕ.ORG: едно копче маха и двата типа мют (по ник и по хост) -
							// сървърът просто игнорира маска, която не съществува
							socket.emit("input", {
								target: channel.id,
								text: "/mode -b m:" + user.nick + "!*@*",
							});
							whoisThenAction((hostname) => {
								socket.emit("input", {
									target: channel.id,
									text: "/mode -b m:*!*@" + hostname,
								});
							});
						},
					},
					{
						label: "Kick",
						type: "item",
						class: "action-kick",
						action() {
							const reason = window.prompt("Причина за изритването:") || "";
							socket.emit("input", {
								target: channel.id,
								text: "/kick " + user.nick + (reason ? " " + reason : ""),
							});
						},
					},
					{
						label: "KickBan (By Nick)",
						type: "item",
						class: "action-kickban-nick",
						action() {
							const reason = window.prompt("Причина за изритването и бана:") || "";
							socket.emit("input", {
								target: channel.id,
								text: "/mode +b " + user.nick + "!*@*",
							});
							socket.emit("input", {
								target: channel.id,
								text: "/kick " + user.nick + (reason ? " " + reason : ""),
							});
						},
					},
					{
						label: "KickBan (By Host)",
						type: "item",
						class: "action-kickban-host",
						action() {
							const reason = window.prompt("Причина за изритването и бана:") || "";
							whoisThenAction((hostname) => {
								socket.emit("input", {
									target: channel.id,
									text: "/mode +b *!*@" + hostname,
								});
								socket.emit("input", {
									target: channel.id,
									text: "/kick " + user.nick + (reason ? " " + reason : ""),
								});
							});
						},
					},
				],
			});
		}
	}

	return items;
}
